
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { generateCashFlow, calculateNPV, calculateIRR, calculatePaybackPeriod } from '@/lib/financial';
import { Calculator, TrendingUp, DollarSign } from 'lucide-react';

export function NPVCalculator() {
  const [params, setParams] = useState({
    initialInvestment: 1000000,
    annualRevenue: 500000,
    annualCosts: 200000,
    growthRate: 5,
    discountRate: 10,
    years: 5
  });

  const [results, setResults] = useState<any>(null);

  const handleCalculate = () => {
    const cashFlowData = generateCashFlow(params);
    const npv = calculateNPV(cashFlowData);
    const irr = calculateIRR(cashFlowData);
    const paybackPeriod = calculatePaybackPeriod(cashFlowData);

    setResults({
      cashFlowData,
      npv,
      irr,
      paybackPeriod
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setParams(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            NPV Calculator
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="initialInvestment">Initial Investment (NZD)</Label>
              <Input
                id="initialInvestment"
                type="number"
                value={params.initialInvestment}
                onChange={(e) => handleInputChange('initialInvestment', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="annualRevenue">Annual Revenue (NZD)</Label>
              <Input
                id="annualRevenue"
                type="number"
                value={params.annualRevenue}
                onChange={(e) => handleInputChange('annualRevenue', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="annualCosts">Annual Costs (NZD)</Label>
              <Input
                id="annualCosts"
                type="number"
                value={params.annualCosts}
                onChange={(e) => handleInputChange('annualCosts', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="growthRate">Growth Rate (%)</Label>
              <Input
                id="growthRate"
                type="number"
                value={params.growthRate}
                onChange={(e) => handleInputChange('growthRate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="discountRate">Discount Rate (%)</Label>
              <Input
                id="discountRate"
                type="number"
                value={params.discountRate}
                onChange={(e) => handleInputChange('discountRate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="years">Years</Label>
              <Input
                id="years"
                type="number"
                value={params.years}
                onChange={(e) => handleInputChange('years', e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleCalculate} className="mt-4">
            Calculate NPV
          </Button>
        </CardContent>
      </Card>

      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  ${results.npv.toLocaleString()}
                </div>
                <p className="text-sm text-gray-600">Net Present Value</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {(results.irr * 100).toFixed(2)}%
                </div>
                <p className="text-sm text-gray-600">Internal Rate of Return</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {results.paybackPeriod.toFixed(1)} years
                </div>
                <p className="text-sm text-gray-600">Payback Period</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
